import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid } from '@mui/material';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { useNewEntityTag } from 'Core/hooks';

//Components
import { SidePanel, SidePanelBody, Form, Select, TimeFrame, useTimeFrameComponent, SidePanelFooter, Button } from '@gpstrackit/gtc-react-controls';
import { toggleModalWorkOrdersScheduleAdvanceFilter, workOrdersStatusSchedule, workOrdersTypeSchedule, workOrdersCustomerSchedule } from './redux/slice';
import { getInitialRange } from "Core/data/Helpers";
import TagsManage from 'Modules/.common/components/TagsManage';
import './index.scss';

const AdvanceFilter = ({ savePreferences, formData, setFormData, applyChanges, hasFilters, initialState }) => {

    const dispatch = useDispatch()
    const { messages } = useIntl()
    const { open, loading, workOrderStatuses, workOrderTypes, customers, loadingCustomers } = useSelector(state => state.workOrdersScheduleAdvanceFilterRedux)
    const dateformat = useSelector(state => state.securityRedux.user.dateformat);

    const formRef = Form.useForm();

    const clearFilters = () => {
        const defaultPreferences = ["severity", "type", "status", "rangeDate", "timeFrame", 'tags']
            .map(key => ({ key, value: null }));

        savePreferences(defaultPreferences)
        setFormData(initialState)
        formRef?.resetValues();
        timeFrame.reset();
        setTimeout(() => {
            formRef?.submit();
        }, 100);
    }

    const entityTags = useNewEntityTag(
        ["Driver"],
        messages['reportsMainRT_viewReportBy'],
        "calc(100vh - 320px)",
        messages['reportsMainRT_runReportFor'],
        'hideTags'
    )

    let severityList = [
        { id: "Low", name: messages['workOrder_createEdit_low'] },
        { id: "Medium", name: messages['workOrder_createEdit_medium'] },
        { id: "High", name: messages['workOrder_createEdit_high'] }
    ]

    const timeFrameOptions = [
        { id: "custom", label: messages['date_custom'] },
        { id: "today", label: messages['date_today'] },
        { id: "yesterday", label: messages['date_yesterday'] },
        { id: "week", label: messages['date_this_week'] },
        { id: "lastWeek", label: messages['date_last_week'] },
        { id: "thisMonth", label: messages['date_this_month'] },
        { id: "nextMonth", label: messages['date_this_nextMonth'] },
        { id: "lastMonth", label: messages['date_last_month'] },
        { id: "quarter", label: messages['date_this_quarter'] },
        { id: "lastQuarter", label: messages['date_last_quarter'] },
        { id: "next7days", label: messages['date_next_7days'] },
        { id: "next15days", label: messages['date_next_15days'] },
        { id: "next30Days", label: messages['date_next_30days'] },
        { id: "next90Days", label: messages['date_next_90days'] },
    ];

    const timeFrame = useTimeFrameComponent(timeFrameOptions, "thisMonth");

    useEffect(() => {
        if (open) {
            dispatch(workOrdersStatusSchedule({}))
            dispatch(workOrdersTypeSchedule({}))
            dispatch(workOrdersCustomerSchedule({}))
        }
    }, [open])

    useEffect(() => {
        if (open && formData) {
            if (formData?.timeFrame && formData?.rangeDate?.start && formData?.rangeDate?.end) {
                let currentTime = timeFrameOptions.find((item) => item?.id === formData.timeFrame)
                timeFrame.onChangeSelect(currentTime, formData.rangeDate.start, formData.rangeDate.end)
            }
            formRef?.setValues(formData);
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            const { startDate, endDate } = timeFrame;
            let date = getInitialRange(timeFrame?.option?.id, startDate, endDate);
            let start = moment(date?.start).utc();
            let end = moment(date?.end).utc();

            let currentDateRange = {
                start: start.toISOString(),
                end: end.toISOString()
            }

            setFormData({ ...formData, rangeDate: currentDateRange, timeFrame: timeFrame?.option?.id })
            savePreferences([
                {
                    key: "rangeDate",
                    value: currentDateRange
                },
                {
                    key: "timeFrame",
                    value: timeFrame?.option?.id
                }
            ])
        }
    }, [timeFrame?.startDate, timeFrame?.endDate]);

    const [selectedItems, setSelectedItems] = useState([{ id: -1, name: "Driver", label: "All" }]);

    return (
        <div className="schedule-advance-filter">
            <SidePanel
                isOpen={open}
                onToggle={() => dispatch(toggleModalWorkOrdersScheduleAdvanceFilter({ open: !open }))}
                style={{ zIndex: 2, width: '320px', transform: 'translateX(320px)' }}
                contentStyle={{ overflowY: "hidden" }}
                classSidePanel={hasFilters() ? "work-order-advanced-filters-available" : ""}
            >
                <SidePanelBody>
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <h2 style={{ margin: 0 }}>
                                {messages['workOrder_tab_advancedFilters_title']}
                            </h2>
                            <span
                                className='btn-link'
                                onClick={clearFilters}
                            >
                                {messages['workOrder_tab_advancedFilters_clearAll']}
                            </span>
                        </Grid>
                    </Grid>
                    <div style={{ width: 270, marginTop: 10 }}>
                        <Form
                            ref={formRef.ref}
                            loading={loading}
                            onValidSubmit={() => {
                                applyChanges()
                                dispatch(toggleModalWorkOrdersScheduleAdvanceFilter({ open: false }))
                            }}
                            onChangeValues={() => { }}
                            className="work-order-advanced-filters"
                        >
                            <Form.Item
                                name="status"
                                label={messages['workOrder_tab_advancedFilters_status']}
                            >
                                <Select
                                    id="status"
                                    type="text"
                                    placeholder={messages['workOrder_tab_advancedFilters_status']}
                                    textField="name"
                                    valueField="id"
                                    isClearable
                                    isSearchable
                                    options={workOrderStatuses || []}
                                    onChange={(value) => {
                                        setFormData({ ...formData, status: value })
                                        savePreferences([{ key: "status", value: value }])
                                    }}
                                />
                            </Form.Item>
                            <Form.Item
                                name="type"
                                label={messages['workOrder_tab_advancedFilters_type']}
                            >
                                <Select
                                    id="type"
                                    type="text"
                                    placeholder={messages['workOrder_tab_advancedFilters_type']}
                                    textField="name"
                                    valueField="id"
                                    isClearable
                                    isSearchable
                                    options={workOrderTypes || []}
                                    onChange={(value) => {
                                        setFormData({ ...formData, type: value })
                                        savePreferences([{ key: "type", value: value }])
                                    }}
                                />
                            </Form.Item>
                            <Form.Item
                                name="severity"
                                label={messages['workOrder_createEdit_severity']}
                            >
                                <Select
                                    id="severity"
                                    type="text"
                                    placeholder={messages['workOrder_createEdit_severity']}
                                    textField="name"
                                    valueField="id"
                                    isClearable
                                    isSearchable
                                    options={severityList}
                                    onChange={(value) => {
                                        setFormData({ ...formData, severity: value })
                                        savePreferences([{ key: "severity", value: value }])
                                    }}
                                />
                            </Form.Item>
                            <div style={{ marginBottom: 16 }}>
                                <Select
                                    name="customers"
                                    textField="name"
                                    label={messages['schedule_workOrder_customer']}
                                    valueField="id"
                                    placeholder={messages['schedule_workOrder_customer']}
                                    isMulti={true}
                                    options={customers || []}
                                    isDisabled={false}
                                    isLoading={loadingCustomers || loading}
                                    isClearable={true}
                                    isSearchable={true}
                                    showPillsOutside={true}
                                    value={formData?.customers || []}
                                    onChange={(value) => {
                                        setFormData({ ...formData, customers: value })
                                        savePreferences([{ key: "customers", value: value }])
                                        formRef?.setValues({
                                            customers: value || null
                                        })
                                    }} />
                            </div>
                            <Form.Item
                                name="scheduledAt"
                                label={messages['workOrder_tab_advancedFilters_scheduledDate']}
                            >
                                <div style={{ width: 270, marginTop: -10 }} className="work-order-advanced-filters-timeframe">
                                    <TimeFrame
                                        isRequired
                                        filterSwitch={false}
                                        {...timeFrame}
                                        maxDate={moment().add(2, 'months').toDate()}
                                        dateFormat={dateformat || 'Y/m/d'}
                                        withTime={false}
                                        customRangeDate={30}
                                        openDateRangePickerModalAutomatically={false}
                                        startDate={moment(formData?.rangeDate?.start)}
                                        endDate={moment(formData?.rangeDate?.end)}

                                    />
                                </div>
                            </Form.Item>
                            <Form.Item
                                name="driver"
                                label={messages['workOrder_tab_advancedFilters_tags']}
                            >
                                <div className="schedule-advance-filter-tag-picker">
                                    <TagsManage
                                        onlyTags={true}
                                        enableSelectAll={false}
                                        disableUntaggedSelection={true}
                                        mode="selector"
                                        height={330}
                                        placeholder={messages["searchTags"] || "Search tags"}
                                        value={formData?.tags || []}
                                        onChange={(items) => {
                                            entityTags.onChange(items);
                                            setFormData({ ...formData, tags: items });
                                            savePreferences([{ key: "tags", value: items }])
                                        }}
                                        isVisiblePill={true}
                                        menuStyle={{ maxHeight: '250px', maxWidth: '250px', overflowY: 'auto' }}
                                    />
                                </div>
                            </Form.Item>
                        </Form>
                    </div>
                </SidePanelBody>
                <SidePanelFooter>
                    <div style={{ display: 'flex', justifyContent: 'end' }}>
                        <Button
                            isDisabled={loading}
                            style={{ marginRight: 8 }}
                            onClick={() => dispatch(toggleModalWorkOrdersScheduleAdvanceFilter({ open: false }))}
                            variant="secondary"
                        >
                            {messages["cancel"]}
                        </Button>
                        <Button
                            isDisabled={loading || (!timeFrame?.startDate || !timeFrame?.endDate)}
                            onClick={() => formRef?.submit()}
                            variant="primary"
                        >
                            {messages["apply"]}
                        </Button>
                    </div>
                </SidePanelFooter>
            </SidePanel>
        </div>
    )
}

export default AdvanceFilter;